<template>
  <div class="ticket-edit-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <feather-icon
              icon="Edit3Icon"
              size="28"
              aria-label="Modifier le ticket de suivi"
            />
          </div>
          <div class="header-text">
            <h1 class="page-title">
              Modifier le ticket de suivi
            </h1>
            <p class="page-subtitle">
              Merci de modifier le formulaire pour mettre à jour votre demande de support liée à vos colis.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="form-container">
      <b-card class="form-card">
        <b-form
          novalidate
          class="ticket-edit-form"
          :disabled="formDisabled || form.closed_at"
          aria-label="Formulaire de modification de ticket de support logistique"
          @submit.prevent="onSubmit"
        >
          <div class="form-section">
            <div class="section-title">
              <feather-icon
                icon="SettingsIcon"
                size="18"
                class="section-icon"
              />
              <span>Informations du ticket</span>
            </div>

            <!-- Catégorie -->
            <b-form-group
              label="Catégorie du ticket"
              label-for="category"
              label-class="form-label-custom"
              :state="categoryState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Sélectionnez la catégorie la plus appropriée. <span class="required-indicator">*</span></span>
              </template>
              <div class="input-wrapper">
                <feather-icon
                  icon="TagIcon"
                  size="18"
                  class="input-icon"
                />
                <v-select
                  id="category"
                  v-model="form.category_id"
                  :options="categoryOptions"
                  :reduce="cat => cat.value"
                  :get-option-label="option => option.label"
                  :clearable="false"
                  placeholder="Sélectionner la catégorie"
                  :class="['custom-select-input', { 'is-invalid': categoryState === false }]"
                  aria-required="true"
                  @input="onCategoryChange"
                />
              </div>
              <b-form-invalid-feedback>
                La catégorie est requise.
              </b-form-invalid-feedback>
              <div
                v-if="selectedCategoryDescription"
                class="category-info-box"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="info-icon"
                />
                <span>{{ selectedCategoryDescription }}</span>
              </div>
            </b-form-group>

            <!-- Titre -->
            <b-form-group
              label="Titre du ticket"
              label-for="title"
              label-class="form-label-custom"
              :state="titleState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Un titre court et explicite pour votre demande de suivi. <span class="required-indicator">*</span></span>
              </template>
              <div class="input-wrapper">
                <feather-icon
                  icon="TypeIcon"
                  size="18"
                  class="input-icon"
                />
                <b-form-input
                  id="title"
                  v-model.trim="form.title"
                  :state="titleState"
                  placeholder="Saisir le titre du ticket de suivi"
                  maxlength="120"
                  autocomplete="off"
                  class="custom-input"
                />
              </div>
              <b-form-invalid-feedback>Le titre est requis.</b-form-invalid-feedback>
            </b-form-group>
          </div>

          <div class="form-section">
            <div class="section-title">
              <feather-icon
                icon="FileTextIcon"
                size="18"
                class="section-icon"
              />
              <span>Détails de la demande</span>
            </div>

            <!-- Description -->
            <b-form-group
              label="Description du problème"
              label-for="description"
              label-class="form-label-custom"
              :state="descState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Décrivez précisément le problème ou la demande de suivi. <span class="required-indicator">*</span></span>
              </template>
              <div class="editor-wrapper">
                <vue-editor
                  id="description"
                  v-model="form.description"
                  :state="descState"
                  placeholder="Décrire le problème ou la demande de suivi"
                  :editor-toolbar="editorToolbar"
                  class="custom-editor"
                />
              </div>
              <b-form-invalid-feedback>La description est requise.</b-form-invalid-feedback>
            </b-form-group>

            <!-- Commandes liées -->
            <b-form-group
              label="Commandes liées"
              label-for="orders"
              label-class="form-label-custom"
              :state="ordersState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Sélectionnez les commandes concernées par ce ticket.</span>
              </template>
              <div class="input-wrapper">
                <feather-icon
                  icon="PackageIcon"
                  size="18"
                  class="input-icon"
                />
                <v-select
                  id="orders"
                  v-model="form.orders"
                  :options="orderOptions"
                  :reduce="order => order.value"
                  :get-option-label="option => option.label"
                  :multiple="true"
                  :clearable="true"
                  placeholder="Sélectionner les commandes"
                  :class="['custom-select-input', { 'is-invalid': ordersState === false }]"
                  label="label"
                >
                  <template #option="option">
                    <span>{{ option.label }}</span>
                  </template>
                  <template #selected-option="option">
                    <span>{{ option.label }}</span>
                  </template>
                </v-select>
              </div>
            </b-form-group>
          </div>

          <!-- Submit Section -->
          <div class="submit-section">
            <b-button
              type="submit"
              class="submit-btn"
              :disabled="loading || form.closed_at"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-2"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="18"
                class="mr-2"
              />
              Enregistrer les modifications
            </b-button>
          </div>

          <!-- Loading Overlay -->
          <div
            v-if="loading"
            class="loading-overlay"
          >
            <b-spinner
              variant="warning"
              label="Chargement..."
            />
          </div>

          <!-- Success Feedback -->
          <transition name="fade">
            <div
              v-if="showSuccess"
              class="success-message"
            >
              <feather-icon
                icon="CheckCircleIcon"
                class="mr-2"
                size="22"
              />
              Ticket modifié avec succès !
            </div>
          </transition>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BForm, BFormGroup, BFormInput, BButton, BSpinner, BFormInvalidFeedback,
} from 'bootstrap-vue'
import { VueEditor } from 'vue2-editor'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import 'vue2-editor/dist/vue2-editor.css'

export default {
  components: {
    vSelect, VueEditor, BCard, BForm, BFormGroup, BFormInput, BButton, BSpinner, BFormInvalidFeedback,
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        category_id: null,
        orders: [],
        submitter: null,
        status: null,
      },
      categories: [],
      ordersList: [],
      loading: false,
      showSuccess: false,
      editorToolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      touched: {
        title: false,
        description: false,
        category: false,
        orders: false,
      },
      selectedCategoryDescription: '',
      user: this.$store?.state?.user || {},
      formDisabled: false,
    }
  },
  computed: {
    categoryOptions() {
      return this.categories.map(cat => ({
        value: cat.id,
        label: cat.display_name || cat.name,
        description: cat.description,
      }))
    },
    orderOptions() {
      return this.ordersList.map(order => ({ value: order.id, label: order.reference || `Commande #${order.id}` }))
    },
    titleState() {
      return this.touched.title ? !!this.form.title : null
    },
    descState() {
      return this.touched.description ? !!this.form.description : null
    },
    categoryState() {
      return this.touched.category ? !!this.form.category_id : null
    },
    ordersState() {
      return null
    },
  },
  watch: {
    'form.title': function handleTitle() { this.touched.title = true },
    'form.description': function handleDescription() { this.touched.description = true },
    'form.category_id': function handleCategory() { this.touched.category = true },
    'form.orders': function handleOrders() { this.touched.orders = true },
  },
  async created() {
    await this.fetchOrders()
    await this.fetchCategories()
    await this.fetchTicket()
    // Permission check: supplier can only edit their own ticket and only if status is default
    if (
      this.user?.role === 'supplier'
      && (this.form.submitter?.id !== this.user.id || !this.form.status?.is_default)
    ) {
      this.formDisabled = true
      this.$bvToast.toast('Vous n\'avez pas la permission de modifier ce ticket.', { variant: 'danger' })
    }
  },
  methods: {
    async fetchCategories() {
      const { data } = await axios.get('/api/support/categories/')
      this.categories = data
    },
    async fetchOrders() {
      // Fetch eligible orders
      const { data: eligibleOrders } = await axios.get('/api/orders/eligible-for-ticket/')
      // If ticket is already loaded, merge its orders
      let ticketOrders = []
      if (this.form && this.form.orders && this.form.orders.length > 0) {
        // If orders are objects, map to id; if already ids, keep as is
        ticketOrders = this.form.orders.map(o => (typeof o === 'object' ? o.id : o))
      }
      // Merge eligible orders with ticket's current orders (avoid duplicates)
      const merged = [...eligibleOrders]
      // Add missing ticket orders
      ticketOrders.forEach(orderId => {
        if (!merged.some(o => o.id === orderId)) {
          // Try to find full order object from ticket (if loaded)
          const orderObj = (this.ticketOrdersFull || []).find(o => o.id === orderId)
          if (orderObj) {
            merged.push(orderObj)
          }
        }
      })
      this.ordersList = merged
    },
    async fetchTicket() {
      const ticketId = this.$route.params.id
      try {
        const { data } = await axios.get(`/api/support/tickets/${ticketId}/`)
        if (data.closed_at) {
          this.$bvToast.toast('Ce ticket est déjà fermé et ne peut plus être modifié.', { variant: 'danger' })
          this.$router.replace({ name: 'SupportTicketDetail', params: { id: ticketId } })
          return
        }
        this.form = {
          ...data,
          orders: data.orders.map(order => order.id), // Ensure orders is an array of ids
          category_id: data.category ? data.category.id : null, // Ensure category_id is set to the id
        }
        // Set touched states
        this.touched.title = true
        this.touched.description = true
        this.touched.category = true
        this.touched.orders = true
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$bvToast.toast('Ce ticket est déjà fermé et ne peut plus être modifié.', { variant: 'danger' })
          this.$router.replace({ name: 'SupportTicketDetail', params: { id: ticketId } })
        } else {
          this.$bvToast.toast('Erreur lors du chargement du ticket.', { variant: 'danger' })
        }
      }
    },
    onCategoryChange() {
      const selectedCategory = this.categories.find(cat => cat.id === this.form.category_id)
      this.selectedCategoryDescription = selectedCategory ? selectedCategory.description : ''
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    async onSubmit() {
      if (this.form.closed_at) {
        this.$bvToast.toast('Ce ticket est déjà fermé et ne peut plus être modifié.', { variant: 'danger' })
        return
      }
      this.loading = true
      try {
        const ticketId = this.$route.params.id
        const payload = {
          ...this.form,
          order_ids: this.form.orders,
        }
        delete payload.orders
        await axios.put(`/api/support/tickets/${ticketId}/`, payload)
        this.showSuccess = true
        // Redirect to detail page after update
        setTimeout(() => {
          this.$router.push({ name: 'SupportTicketDetail', params: { id: ticketId } })
        }, 1200)
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la modification du ticket.', { variant: 'danger' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* Container */
.ticket-edit-container {
  min-height: 100vh;
  background: #ffffff;
  padding: 0;
}

/* Header Section */
.page-header {
  background: #ffffff;
  padding: 2rem 2.5rem;
  margin-bottom: 0;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.header-icon {
  width: 56px;
  height: 56px;
  background: #0A2634;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 15px rgba(10, 38, 52, 0.18);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0A2634;
  margin: 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0.25rem 0 0 0;
}

/* Form Container */
.form-container {
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
}

.form-card {
  background: white;
  border-radius: 0;
  border: none;
  box-shadow: none;
  overflow: hidden;
}

.form-card >>> .card-body {
  padding: 2.5rem 3rem;
}

/* Form Sections */
.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f1f5f9;
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0A2634;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #0A2634;
  width: fit-content;
}

.section-icon {
  color: #0A2634;
}

/* Form Groups */
.form-group-custom {
  margin-bottom: 1.75rem;
}

.form-label-custom {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.field-description {
  color: #64748b;
  font-size: 0.85rem;
}

.required-indicator {
  color: #0A2634;
  font-weight: 700;
}

/* Input Wrapper */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 1rem;
  z-index: 2;
  color: #94a3b8;
  transition: color 0.2s;
}

.input-wrapper:focus-within .input-icon {
  color: #0A2634;
}

/* Custom Input */
.custom-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.custom-input:focus {
  border-color: #0A2634;
  background: white;
  box-shadow: 0 0 0 4px rgba(10, 38, 52, 0.12);
  outline: none;
}

.custom-input::placeholder {
  color: #94a3b8;
}

/* Custom Select */
.custom-select-input {
  width: 100%;
}

.custom-select-input >>> .vs__dropdown-toggle {
  padding: 0.5rem 1rem 0.5rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  min-height: 50px;
  transition: all 0.2s ease;
}

.custom-select-input >>> .vs__dropdown-toggle:focus-within {
  border-color: #0A2634;
  background: white;
  box-shadow: 0 0 0 4px rgba(10, 38, 52, 0.12);
}

.custom-select-input >>> .vs__selected {
  background: #0A2634;
  color: white;
  border-radius: 8px;
  padding: 0.25rem 0.75rem;
  margin: 0.25rem;
  font-weight: 500;
  border: none;
}

.custom-select-input >>> .vs__deselect {
  fill: white;
}

.custom-select-input >>> .vs__dropdown-menu {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.custom-select-input >>> .vs__dropdown-option {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  transition: all 0.15s;
}

.custom-select-input >>> .vs__dropdown-option--highlight {
  background: #e1effe;
  color: #0A2634;
}

.custom-select-input >>> .vs__dropdown-option--selected {
  background: #0A2634;
  color: white;
}

.custom-select-input >>> .vs__search::placeholder {
  color: #94a3b8;
}

/* Editor Wrapper */
.editor-wrapper {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.editor-wrapper:focus-within {
  border-color: #0A2634;
  background: white;
  box-shadow: 0 0 0 4px rgba(10, 38, 52, 0.12);
}

.custom-editor {
  min-height: 180px;
}

.custom-editor >>> .ql-toolbar {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  background: white;
}

.custom-editor >>> .ql-container {
  border: none;
  font-size: 0.95rem;
}

.custom-editor >>> .ql-editor {
  min-height: 150px;
  padding: 1rem;
}

.custom-editor >>> .ql-editor.ql-blank::before {
  color: #94a3b8;
  font-style: normal;
}

/* Category Info Box */
.category-info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  background: #e1effe;
  border-left: 3px solid #0A2634;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1e293b;
}

.info-icon {
  color: #0A2634;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Submit Section */
.submit-section {
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
  margin-top: 1.5rem;
}

.submit-btn {
  background: #0A2634;
  border: none;
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 1rem 2.5rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(10, 38, 52, 0.2);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(10, 38, 52, 0.28);
  background: #12384a;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  z-index: 10;
}

/* Success Message */
.success-message {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(5, 150, 105, 0.1);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 1.5rem 1rem;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .page-title {
    font-size: 1.35rem;
  }

  .form-card >>> .card-body {
    padding: 1.5rem;
  }

  .submit-btn {
    width: 100%;
  }
}
</style>
